import { useState } from 'react';
import { useContainer } from '../providers/container-context';
import { useAsync, useDebounced } from '../hooks/use-async';
import { useIsMobile } from '../hooks/use-is-mobile';
import type { AuditLogEntry, AuditLogQuery } from '@domain/entities';
import {
  Drawer,
  EmptyState,
  ErrorState,
  FilterPills,
  Pager,
  RefreshButton,
  SearchInput,
  TableSkeleton,
} from '../components/ui';

/* ------------------------------------------------------------------ */
/* Constantes de filtre                                                 */
/* ------------------------------------------------------------------ */

const ACTOR_FILTERS = [
  { value: 'all' as const, label: 'Tous' },
  { value: 'admin' as const, label: 'Admin' },
  { value: 'user' as const, label: 'Utilisateur' },
  { value: 'system' as const, label: 'Système' },
];

const ENTITY_OPTIONS = [
  '', 'Business', 'Offer', 'BusinessSubscription', 'User', 'Review',
  'PaymentIntent', 'Article', 'DirectoryEntry', 'Appointment',
  'Formula', 'City', 'OfferCategory', 'ArticleCategory',
  'DirectoryCategory', 'AppointmentReason', 'BusinessType', 'RefCountry',
];

const ACTION_OPTIONS = [
  '', 'created', 'updated', 'deleted', 'approved', 'rejected',
  'activated', 'expired', 'flagged', 'dismissed', 'cancelled',
  'failed', 'succeeded', 'toggled', 'expiration-reminder-sent', 'published',
];

/* ------------------------------------------------------------------ */
/* Badges colorés                                                       */
/* ------------------------------------------------------------------ */

const ACTION_PALETTE: Record<string, readonly [string, string]> = {
  created:   ['#5CA98922', '#2E6B4E'],
  activated: ['#5CA98922', '#2E6B4E'],
  approved:  ['#5CA98922', '#2E6B4E'],
  published: ['#5CA98922', '#2E6B4E'],
  succeeded: ['#5CA98922', '#2E6B4E'],
  updated:   ['#7FB3D522', '#33506B'],
  toggled:   ['#7FB3D522', '#33506B'],
  flagged:   ['#E0A55222', '#8A5B22'],
  cancelled: ['#E0A55222', '#8A5B22'],
  expired:   ['#E0A55222', '#8A5B22'],
  'expiration-reminder-sent': ['#E0A55222', '#8A5B22'],
  deleted:   ['#C6564F22', '#8A4A44'],
  rejected:  ['#C6564F22', '#8A4A44'],
  failed:    ['#C6564F22', '#8A4A44'],
  dismissed: ['#8A95A322', '#5A6673'],
};

const ACTOR_PALETTE: Record<string, readonly [string, string]> = {
  admin:  ['#7FB3D522', '#33506B'],
  user:   ['#5CA98922', '#2E6B4E'],
  system: ['#8A95A322', '#5A6673'],
};

function ActionBadge({ action }: { action: string }): JSX.Element {
  const [bg, fg] = ACTION_PALETTE[action] ?? ['#8A95A322', '#5A6673'];
  return (
    <span className="bbm-badge" style={{ background: bg, color: fg, fontWeight: 600 }}>
      {action}
    </span>
  );
}

function ActorBadge({ actorType }: { actorType: string }): JSX.Element {
  const [bg, fg] = ACTOR_PALETTE[actorType] ?? ['#8A95A322', '#5A6673'];
  return (
    <span className="bbm-badge" style={{ background: bg, color: fg }}>
      {actorType}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Formatage                                                            */
/* ------------------------------------------------------------------ */

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function fmtDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR');
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR');
}

/* ------------------------------------------------------------------ */
/* Drawer détail                                                        */
/* ------------------------------------------------------------------ */

function EntryDrawer({ entry, onClose }: { entry: AuditLogEntry; onClose: () => void }): JSX.Element {
  return (
    <Drawer
      title={`${entry.entity} · ${entry.action}`}
      subtitle={fmtDate(entry.createdAt)}
      onClose={onClose}
    >
      <ActionBadge action={entry.action} />

      <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div className="bbm-detail-label">Entité</div>
          <div className="bbm-detail-value">{entry.entity}</div>
        </div>
        <div>
          <div className="bbm-detail-label">Entité ID</div>
          <div className="bbm-detail-value" style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
            {entry.entityId}
          </div>
        </div>
        <div>
          <div className="bbm-detail-label">Acteur</div>
          <div className="bbm-detail-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ActorBadge actorType={entry.actorType} />
            {entry.actorLabel && entry.actorLabel !== entry.actorType && (
              <span>{entry.actorLabel}</span>
            )}
          </div>
        </div>
        {entry.actorId && (
          <div>
            <div className="bbm-detail-label">Acteur ID</div>
            <div className="bbm-detail-value" style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
              {entry.actorId}
            </div>
          </div>
        )}
        <div>
          <div className="bbm-detail-label">Date</div>
          <div className="bbm-detail-value">{fmtDate(entry.createdAt)}</div>
        </div>
        <div>
          <div className="bbm-detail-label">Snapshot</div>
          <pre style={{
            marginTop: 6,
            background: '#F4F6F9',
            borderRadius: 8,
            padding: '12px 14px',
            fontSize: 11.5,
            lineHeight: 1.6,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            border: '1px solid #E4E8ED',
          }}>
            {JSON.stringify(entry.payload, null, 2)}
          </pre>
        </div>
        {entry.meta && Object.keys(entry.meta).length > 0 && (
          <div>
            <div className="bbm-detail-label">Meta</div>
            <pre style={{
              marginTop: 6,
              background: '#F4F6F9',
              borderRadius: 8,
              padding: '12px 14px',
              fontSize: 11.5,
              lineHeight: 1.6,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              border: '1px solid #E4E8ED',
            }}>
              {JSON.stringify(entry.meta, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/* Page principale                                                      */
/* ------------------------------------------------------------------ */

type ActorFilter = 'all' | 'admin' | 'user' | 'system';

export function AuditLogPage(): JSX.Element {
  const container = useContainer();
  const isMobile = useIsMobile();

  const [actorFilter, setActorFilter] = useState<ActorFilter>('all');
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [entityIdInput, setEntityIdInput] = useState('');
  const entityId = useDebounced(entityIdInput, 400);

  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);

  const query: AuditLogQuery = {
    ...(entity ? { entity } : {}),
    ...(actorFilter !== 'all' ? { actorType: actorFilter } : {}),
    ...(action ? { action } : {}),
    ...(from ? { from: `${from}T00:00:00Z` } : {}),
    ...(to ? { to: `${to}T23:59:59Z` } : {}),
    ...(entityId ? { entityId } : {}),
    page,
    pageSize: 50,
  };

  const resource = useAsync(
    () => container.auditLog.search.execute(query),
    [container, entity, actorFilter, action, from, to, entityId, page],
  );

  const hasFilters = Boolean(entity || actorFilter !== 'all' || action || from || to || entityId);

  const resetFilters = (): void => {
    setActorFilter('all');
    setEntity('');
    setAction('');
    setFrom('');
    setTo('');
    setEntityIdInput('');
    setPage(1);
  };

  const rows = resource.data?.data ?? [];

  return (
    <>
      {/* ---- Toolbar ---- */}
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <FilterPills
            options={ACTOR_FILTERS}
            value={actorFilter}
            onChange={(next) => { setActorFilter(next); setPage(1); }}
          />
          <select
            className="bbm-input"
            value={entity}
            aria-label="Entité"
            style={{ width: 160 }}
            onChange={(e) => { setEntity(e.target.value); setPage(1); }}
          >
            {ENTITY_OPTIONS.map((o) => <option key={o} value={o}>{o || 'Toutes les entités'}</option>)}
          </select>

          <select
            className="bbm-input"
            value={action}
            aria-label="Action"
            style={{ width: 150 }}
            onChange={(e) => { setAction(e.target.value); setPage(1); }}
          >
            {ACTION_OPTIONS.map((o) => <option key={o} value={o}>{o || 'Toutes les actions'}</option>)}
          </select>

          <input
            className="bbm-input"
            type="date"
            value={from}
            aria-label="Depuis"
            style={{ width: 145 }}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }}
          />

          <input
            className="bbm-input"
            type="date"
            value={to}
            aria-label="Jusqu'à"
            style={{ width: 145 }}
            onChange={(e) => { setTo(e.target.value); setPage(1); }}
          />

          <SearchInput
            value={entityIdInput}
            placeholder="Rechercher par ID entité…"
            onChange={setEntityIdInput}
            width={220}
          />

          {hasFilters && (
            <button type="button" className="bbm-btn bbm-btn--ghost" onClick={resetFilters}>
              Réinitialiser
            </button>
          )}
        </div>
        <div className="bbm-toolbar__end">
          <RefreshButton onRefresh={resource.reload} loading={resource.loading} />
        </div>
      </div>

      {/* ---- Chargement ---- */}
      {resource.loading ? <TableSkeleton /> : null}

      {/* ---- Erreur ---- */}
      {resource.error ? (
        <ErrorState
          status={resource.error.status}
          message={
            resource.error.status === 429
              ? 'Trop de requêtes — patientez quelques secondes puis réessayez.'
              : resource.error.message
          }
          onRetry={resource.reload}
        />
      ) : null}

      {/* ---- Vide ---- */}
      {!resource.loading && !resource.error && rows.length === 0 ? (
        <EmptyState
          title="Aucun événement"
          body={hasFilters ? 'Aucun événement ne correspond aux filtres.' : 'Le journal est vide pour le moment.'}
          actionLabel={hasFilters ? 'Réinitialiser les filtres' : undefined}
          onAction={hasFilters ? resetFilters : undefined}
        />
      ) : null}

      {/* ---- Liste ---- */}
      {!resource.loading && !resource.error && rows.length > 0 ? (
        <>
          {isMobile ? (
            /* ---- Vue mobile : cards ---- */
            <div className="bbm-card-stack">
              {rows.map((row) => (
                <div key={row.id} className="bbm-mobile-card">
                  <div className="bbm-mobile-card__head">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                      <div className="bbm-mobile-card__title">{row.entity}</div>
                      <div className="bbm-mobile-card__sub">
                        {fmtDateShort(row.createdAt)} · {fmtTime(row.createdAt)}
                      </div>
                    </div>
                    <ActionBadge action={row.action} />
                  </div>
                  <div className="bbm-mobile-card__fields">
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Acteur</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ActorBadge actorType={row.actorType} />
                        {row.actorLabel && row.actorLabel !== row.actorType && (
                          <span style={{ fontSize: 12 }}>{row.actorLabel}</span>
                        )}
                      </span>
                    </div>
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">ID entité</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {row.entityId.slice(0, 12)}…
                      </span>
                    </div>
                  </div>
                  <div className="bbm-mobile-card__actions">
                    <button
                      type="button"
                      className="bbm-btn"
                      onClick={() => setSelected(selected?.id === row.id ? null : row)}
                    >
                      Voir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* ---- Vue desktop : panel/rows ---- */
            <div className="bbm-panel">
              <div className="bbm-row bbm-row--head">
                <div style={{ width: 140 }}>Date / heure</div>
                <div style={{ width: 150 }}>Entité</div>
                <div style={{ width: 160 }}>Action</div>
                <div style={{ width: 120 }}>Acteur</div>
                <div style={{ width: 160 }}>Label acteur</div>
                <div style={{ marginLeft: 'auto' }}>Actions</div>
              </div>

              {rows.map((row) => (
                <div key={row.id} className="bbm-row">
                  <div className="bbm-cell" style={{ width: 140 }}>
                    {fmtDateShort(row.createdAt)}
                    <span style={{ display: 'block', fontSize: 11, color: '#8A95A3' }}>
                      {fmtTime(row.createdAt)}
                    </span>
                  </div>
                  <div className="bbm-cell-strong" style={{ width: 150 }}>
                    {row.entity}
                  </div>
                  <div style={{ width: 160 }}>
                    <ActionBadge action={row.action} />
                  </div>
                  <div style={{ width: 120 }}>
                    <ActorBadge actorType={row.actorType} />
                  </div>
                  <div className="bbm-cell" style={{ width: 160 }}>
                    {row.actorLabel ?? '—'}
                  </div>
                  <div className="bbm-row__actions">
                    <button
                      type="button"
                      className="bbm-btn"
                      onClick={() => setSelected(selected?.id === row.id ? null : row)}
                    >
                      Voir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {resource.data && resource.data.pageCount > 1 && (
            <Pager
              page={resource.data.page}
              pageCount={resource.data.pageCount}
              total={resource.data.total}
              pageSize={resource.data.pageSize}
              onChange={setPage}
            />
          )}
        </>
      ) : null}

      {/* ---- Drawer détail ---- */}
      {selected ? <EntryDrawer entry={selected} onClose={() => setSelected(null)} /> : null}
    </>
  );
}
